import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';

interface Community {
  club_id: string;
  club_name: string;
  logo?: string;
  city?: string;
  member_count?: number;
}

interface PopularCommunitiesProps {
  communities: Community[];
  isLoading?: boolean;
}

const PopularCommunities: React.FC<PopularCommunitiesProps> = ({
  communities,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const handleCommunityClick = (clubId: string) => {
    navigate(`/dashboard/clubs/${clubId}`);
  };

  const handleViewAllClick = () => {
    navigate('/dashboard/facilities');
  };

  if (isLoading) {
    return (
      <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-40 bg-dark-300 rounded"></div>
          <div className="h-6 w-24 bg-dark-300 rounded"></div>
        </div>
        <div className="flex overflow-x-auto space-x-4 pb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-dark-300 rounded-lg h-48 w-48 flex-shrink-0"></div>
          ))}
        </div>
      </div>
    );
  }

  if (communities.length === 0) return null;

  return (
    <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Shield className="h-5 w-5 text-gold-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">Popular Communities</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewAllClick}
          className="text-dark-800 hover:text-white"
        >
          View All
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      <div className="flex overflow-x-auto space-x-4 pb-2">
        {communities.map((community) => (
          <button
            key={community.club_id}
            className="bg-dark-300 rounded-lg p-4 hover:bg-dark-400 transition-colors border border-dark-400 hover:border-gold-400 flex-shrink-0 w-[200px] cursor-pointer text-left"
            onClick={() => handleCommunityClick(community.club_id)}
          >
            <div className="flex flex-col items-center">
              <div className="mb-3">
                {community.logo ? (
                  <img
                    src={community.logo}
                    alt={community.club_name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-dark-400"
                    onError={(e) => {
                      const target = e.currentTarget;
                      const fallbackDiv = document.createElement('div');
                      fallbackDiv.className = 'w-16 h-16 rounded-full bg-dark-400 flex items-center justify-center border-2 border-dark-400';
                      fallbackDiv.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gold-400"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`;
                      target.parentNode?.replaceChild(fallbackDiv, target);
                    }}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-dark-400 flex items-center justify-center border-2 border-dark-400">
                    <Shield className="h-8 w-8 text-gold-400" />
                  </div>
                )}
              </div>
              <h4 className="font-medium text-white text-sm text-center mb-1 truncate w-full">
                {community.club_name}
              </h4>
              {community.city && (
                <div className="text-dark-800 text-xs text-center truncate w-full">
                  {community.city}
                </div>
              )}
              {typeof community.member_count === 'number' && (
                <div className="text-dark-800 text-xs text-center mt-1">
                  {community.member_count} {community.member_count === 1 ? 'member' : 'members'}
                </div>
              )}
            </div>
            <div className="mt-3 pt-2 border-t border-dark-400">
              <Button
                className="w-full bg-dark-400 hover:bg-dark-500 dark:hover:bg-dark-500 hover:bg-dark-300 text-white text-xs py-1"
                size="sm"
              >
                View Club
              </Button>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default PopularCommunities;